import React from 'react';
import type { Category } from '../../types';

interface CategoryListProps {
  categories: Category[];
  selectedCategoryId: number | string | null;
  onSelectCategory: (id: number | string) => void;
  onMoveCategory?: (categoryId: number | string, direction: 'up' | 'down') => void;
  onReorderCategory?: (categoryId: number | string, targetIndex: number) => void;
}

const CategoryList: React.FC<CategoryListProps> = ({ 
  categories, 
  selectedCategoryId, 
  onSelectCategory,
  onMoveCategory,
  onReorderCategory
}) => {
  const handleDragStart = (event: React.DragEvent<HTMLButtonElement>, categoryId: number | string) => {
    if (!onReorderCategory) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(categoryId));
  };

  const handleDragOver = (event: React.DragEvent<HTMLLIElement>) => {
    if (!onReorderCategory) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLLIElement>, targetIndex: number) => {
    if (!onReorderCategory) return;
    event.preventDefault();
    const draggedId = Number(event.dataTransfer.getData('text/plain'));
    if (!draggedId || Number.isNaN(draggedId)) return;
    onReorderCategory(draggedId, targetIndex);
  };
  return (
    <aside className="w-full md:w-48 lg:w-56 bg-white flex-shrink-0 flex flex-col md:border-r border-gray-200">
      <div className="p-2 md:p-4 flex-shrink-0">
        <h2 className="text-xs md:text-base lg:text-lg font-bold text-gray-700">Categories</h2>
      </div>
      <nav className="flex-1 overflow-x-auto md:overflow-y-auto overflow-y-hidden px-2 md:px-4 pb-2 md:pb-4 flex md:block">
        <ul className="flex md:flex-col gap-2 md:gap-0 min-w-0 md:min-w-none">
          {categories.map((category, index) => (
            <li 
              key={category.id} 
              className="flex items-center gap-0.5 md:gap-1 mb-0 md:mb-2 flex-shrink-0 md:flex-shrink"
              onDragOver={handleDragOver}
              onDrop={(event) => handleDrop(event, index)}
            >
              <button
                draggable={!!onReorderCategory}
                onDragStart={(event) => handleDragStart(event, category.id)}
                onClick={() => onSelectCategory(category.id)}
                className={`flex-1 text-left px-3 py-2.5 md:py-2 rounded-lg transition-colors duration-200 text-xs md:text-sm min-h-[44px] md:min-h-0 flex items-center ${
                  selectedCategoryId != null && (selectedCategoryId === category.id || String(selectedCategoryId) === String(category.id))
                    ? 'bg-pink-500 text-white font-semibold'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
              {onMoveCategory && (
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveCategory(category.id, 'up');
                    }}
                    disabled={index === 0}
                    className={`px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs rounded ${
                      index === 0
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveCategory(category.id, 'down');
                    }}
                    disabled={index === categories.length - 1}
                    className={`px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs rounded ${
                      index === categories.length - 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default CategoryList;
